import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useReactToPrint } from "react-to-print";
import FloatButton from "./ui/FloatButton";
import { theme } from "../theme";
import { WatchContext } from "../WatchContext";
import OutputSummaryTable from "./OutputSummaryTable";
import SedimentSummary from "./SedimentSummary";
import SoilSummary from "./SoilSummary";
import WaterSummary from "./WaterSummary";
import calcOverall from "../tools/calcOverall";
import { sedimentAnalysis } from "../tools/sediment";
import RenderIf from "./RenderIf";
import BackToDash from "./BackToDash";
import ValuesTable from "./ValuesTable";
import Justifications from "./Justifications";
import { CSVLink } from "react-csv";
import PermitStatus from "./PermitStatus";

let pdeBlue = theme.colors.pdeBlue

const Styles = {
  Input: styled.input`
    padding: 1.2rem;
    margin: 1rem;
  `,
  FormDiv: styled.div`
    display: flex;
    flex-direction: column;
    margin: 2rem;
    span {
      margin: 1rem;
      font-size: 1.6rem;
    }
  `,
  ReportTitle: styled.span`
    font-size: 1.6rem;
    font-weight: bold;
    margin: 0 1rem;
  `,
  TitleDiv: styled.div`
    margin: 1rem;
  `,
  ReportSummary: styled.div`
    margin-top: 2rem;
    p {
      margin: 1rem;
    }
  `,
  ButtonDiv: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  ProgDiv: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 2rem;
  `,
};

const PrintButton = ({ handlePrint }) => (
  <div style={{ maxWidth: "800px" }}>
    <FloatButton
      textColor="white"
      bgColor={pdeBlue}
      handleClick={handlePrint}
      width="200px"
    >
      Print Report*
    </FloatButton>
    <span style={{ fontSize: "1.2rem" }}>
      *Please note that different browsers will print this page differently. At
      the time of the creation of this website, Chrome and Safari were
      generating a slightly better table structure than Firefox.{" "}
    </span>
  </div>
);

const PrintReport = () => {
  const [siteName, setSiteName] = useState("");
  const [analyst, setAnalyst] = useState("");
  const [org, setOrg] = useState("");

  let {
    horizontal,
    vertical,
    hydrology,
    biology,
    sediment,
    vertsed,
    horsed,
    biosed,
    hydrosed,
    water,
  } = useContext(WatchContext);
  let topicArray = [horizontal, vertical, hydrology, biology];

  let sedAnal = sedimentAnalysis({ vertsed, horsed, biosed, hydrosed });
  let overallStatus = calcOverall(topicArray);

  const siteNameRef = useRef("");
  const analystRef = useRef("");
  const orgRef = useRef("");
  const toPrintRef = useRef();

  let date = new Date();
  let today = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  let dateString = `${date.getFullYear()}_${
    date.getMonth() + 1
  }_${date.getDate()} `;

  // when the page loads, put the cursor and focus on the site name input
  useEffect(() => {
    siteNameRef.current.focus();
  }, []);

  const handlePrint = useReactToPrint({
    content: () => toPrintRef.current,
  });

  // prepare data for csv download
  let csvData = [ horizontal,
    vertical,
    hydrology,
    biology,
    sediment,
    vertsed,
    horsed,
    biosed,
    hydrosed,
    water,].map(item => {
      delete item.metrics
      delete item.methods
      return item
    })
  

  return (
    <div>
      <BackToDash />
      <Styles.FormDiv>
        <span>Please enter the following details for your report:</span>
        Site Name:{" "}
        <Styles.Input
          onChange={() => setSiteName(siteNameRef.current.value)}
          ref={siteNameRef}
          type={"text"}
        />
        Analyst:{" "}
        <Styles.Input
          onChange={() => setAnalyst(analystRef.current.value)}
          ref={analystRef}
          type={"text"}
        />
        Organization:{" "}
        <Styles.Input
          onChange={() => setOrg(orgRef.current.value)}
          ref={orgRef}
          type={"text"}
        />
      </Styles.FormDiv>
      <PrintButton handlePrint={handlePrint} />

      <div style={{ width: "750px", marginLeft: "1rem" }} ref={toPrintRef}>
        <Styles.TitleDiv>
          <Styles.ReportTitle>
            <u>Wetlands Assessment Tool for Condition & Health - Report</u>
          </Styles.ReportTitle>
        </Styles.TitleDiv>
        <Styles.TitleDiv>
          <Styles.ReportTitle>Site Name: {siteName}</Styles.ReportTitle>
          <br />
          <Styles.ReportTitle>Date: {today}</Styles.ReportTitle>
          <br />
          <Styles.ReportTitle>Analyst: {analyst}</Styles.ReportTitle>
          <br />
          <Styles.ReportTitle>Organization: {org}</Styles.ReportTitle>
          <br />
        </Styles.TitleDiv>

        <Styles.ReportSummary>
          <Styles.ReportTitle>
            <u>Report Summary</u>
            {console.log('printReport test', overallStatus)}
          </Styles.ReportTitle>
          {overallStatus?.id === 5 ? (
            <p>More information is needed to generate an assessment.</p>
          ) : (
            <p>
              
              Evaluation of the provided data indicates{" "}
              <i>{overallStatus?.summaryText}</i> the{" "}
              <i>{overallStatus?.attribs.join(", ")}</i> attribute
              {overallStatus?.attribs?.length > 1 ? "s" : ""}.
              <RenderIf isTrue={sediment.complete}>
                {" "}
                The site exhibits{" "}
                <i>{vertsed?.currentConcern === 0 ? "good" : "poor"}</i>{" "}
                vertical building and{" "}
                <i>{horsed?.currentConcern === 0 ? "the" : "poor"}</i> ability
                to prograde. Accretion is{" "}
                <i>
                  {JSON.stringify(sedAnal?.accumulation.score) === "[0,0]"
                    ? "likely"
                    : "not likely"}
                </i>{" "}
                to persist and declining elevation due to decomposition is{" "}
                <i>{biosed?.currentConcern === 0 ? "not" : null}</i> a concern.
              </RenderIf>
              <RenderIf isTrue={water?.salinityStatus?.level?.length > 0}>
                {" "}
                Salinity is{" "}
                <i>
                  {water?.salinityStatus?.level}{" "}
                  {water?.salinityStatus?.shellfish?.length >= 1
                    ? `which is appropriate for ${water?.salinityStatus?.shellfish}`
                    : null}{" "}
                  {water?.salinityStatus?.carbon?.length >= 1
                    ? `and suggests ${water?.salinityStatus?.carbon} carbon sequestration potential`
                    : null}
                </i>
                .
              </RenderIf>
            </p>
          )}
        </Styles.ReportSummary>
        {/* <Styles.ProgDiv>
             <Progress data={horizontal}/>
            <Progress data={vertical}/>
            <Progress data={biology}/>
            <Progress data={hydrology}/>
      </Styles.ProgDiv> */}
        <Styles.ProgDiv>
          <OutputSummaryTable />
        </Styles.ProgDiv>
        <Styles.ProgDiv>
          <SedimentSummary />
        </Styles.ProgDiv>
        <Styles.ProgDiv>
          <SoilSummary />
        </Styles.ProgDiv>
        <Styles.ProgDiv>
          <WaterSummary />
        </Styles.ProgDiv>
        <Styles.ProgDiv>
          <PermitStatus/>
        </Styles.ProgDiv>
        <ValuesTable />
        <Justifications/>
      </div>
      
      <PrintButton handlePrint={handlePrint} />
      <CSVLink
          style={{ textDecoration: "none" }}
          data={csvData}
          filename={`pdeWatchEntrie-${dateString}.csv`}
        >
      <FloatButton bgColor={pdeBlue+30} width="200px">Export entered values to csv</FloatButton>
      </CSVLink>
      <BackToDash />
      
    </div>
  );
};

export default PrintReport;
